import type { FC } from 'react';

import Avatar from '@/common-components/Avatar';
import Pill from '@/common-components/Pill';
import { timeAgo } from '@/utils/date.util';
import type { AdminTargetReport } from '@/types/moderation.types';

interface ReportsListProps {
  reports: AdminTargetReport[];
  /** Server-side total; larger than `reports.length` when the cap trimmed the list. */
  totalCount: number;
}

const STATUS_TONE = {
  open: 'danger',
  reviewing: 'warn',
  resolved: 'success',
} as const;

/**
 * The reports filed against one listing or shoutt.
 *
 * The drawers used to show `Reports: ⚠️ 1` and nothing else, which tells a moderator that
 * something is wrong but not what — the reason is the whole decision.
 */
const ReportsList: FC<ReportsListProps> = ({ reports, totalCount }) => {
  if (totalCount === 0) {
    return <p className="py-1 text-[12.5px] text-[var(--color-ink-muted)]">No reports.</p>;
  }

  return (
    <div className="flex flex-col gap-2.5 pt-1">
      {reports.map((report) => (
        <div
          key={report.id}
          className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] p-3"
        >
          <div className="flex items-center gap-2">
            {report.origin === 'ai' ? (
              <Pill tone="pink" className="text-[10px]">🤖 AI Moderation</Pill>
            ) : (
              <>
                <Avatar name={report.reporter.name} src={report.reporter.avatarUrl} size="sm" />
                <span className="truncate text-[12px] font-semibold text-[var(--color-ink)]">
                  {report.reporter.name}
                </span>
              </>
            )}
            <Pill tone={STATUS_TONE[report.status]} className="ml-auto text-[10px]">
              {report.status}
            </Pill>
          </div>

          <p className="mt-1.5 text-[12.5px] leading-relaxed text-[var(--color-ink)]">
            “{report.reason}”
          </p>

          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[var(--color-ink-soft)]">
            <span>{timeAgo(report.createdAt)}</span>
            {report.resolutionNote ? (
              <span className="text-[var(--color-success-ink)]">
                · Resolved: {report.resolutionNote}
              </span>
            ) : null}
          </div>
        </div>
      ))}

      {totalCount > reports.length ? (
        <p className="text-[11.5px] text-[var(--color-ink-soft)]">
          Showing the {reports.length} most recent of {totalCount} reports.
        </p>
      ) : null}
    </div>
  );
};

export default ReportsList;
